import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import './ArchitectDecisionsTour.css';

export const ARCHITECT_DECISIONS_TOUR_KEY = 'lctf:architectDecisionsTour:v1';

export type ArchitectDecisionsTourScript = 'staff' | 'architect';

export function architectDecisionsTourStorageKey(script: ArchitectDecisionsTourScript): string {
  return `${ARCHITECT_DECISIONS_TOUR_KEY}:${script}`;
}

const STAFF_STEPS = [
  {
    title: 'Comments stay with the team',
    body: 'Hash out details here so the architect is not in the weeds.',
  },
  {
    title: 'Architect Discussion when it matters',
    body: 'Open the Architect Discussion tab to loop them in on the decision, not the whole thread.',
  },
  {
    title: 'That’s it',
    body: 'You can replay this from help whenever you need a reminder.',
  },
];

const ARCHITECT_STEPS = [
  {
    title: 'Design decisions',
    body: 'When LCTF needs your call, it shows up here.',
  },
  {
    title: 'Your drawings',
    body: 'You only see the boards we shared, and pins that ask for you.',
  },
];

export function hasSeenArchitectDecisionsTour(script: ArchitectDecisionsTourScript = 'staff'): boolean {
  try {
    if (localStorage.getItem(architectDecisionsTourStorageKey(script))) return true;
    // Legacy single key counts as staff-seen so existing team members are not re-toured.
    return script === 'staff' && !!localStorage.getItem(ARCHITECT_DECISIONS_TOUR_KEY);
  } catch {
    return false;
  }
}

export function markArchitectDecisionsTourSeen(script: ArchitectDecisionsTourScript = 'staff'): void {
  try {
    localStorage.setItem(architectDecisionsTourStorageKey(script), new Date().toISOString());
  } catch {
    /* private mode */
  }
}

export function clearArchitectDecisionsTourSeen(script?: ArchitectDecisionsTourScript): void {
  try {
    if (script) {
      localStorage.removeItem(architectDecisionsTourStorageKey(script));
      return;
    }
    localStorage.removeItem(ARCHITECT_DECISIONS_TOUR_KEY);
    localStorage.removeItem(architectDecisionsTourStorageKey('staff'));
    localStorage.removeItem(architectDecisionsTourStorageKey('architect'));
  } catch {
    /* ignore */
  }
}

export interface ArchitectDecisionsTourProps {
  script: ArchitectDecisionsTourScript;
  open: boolean;
  onClose: () => void;
}

export const ArchitectDecisionsTour: React.FC<ArchitectDecisionsTourProps> = ({
  script,
  open,
  onClose,
}) => {
  const steps = script === 'architect' ? ARCHITECT_STEPS : STAFF_STEPS;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  const finish = useCallback(() => {
    markArchitectDecisionsTourSeen(script);
    onClose();
  }, [onClose, script]);

  if (!open) return null;
  const step = steps[index];
  const last = index === steps.length - 1;

  return (
    <div className="adt-backdrop" role="presentation">
      <div className="adt-card" role="dialog" aria-labelledby="adt-title">
        <button type="button" className="adt-close" onClick={finish} aria-label="Skip">
          <X size={14} />
        </button>
        <p className="adt-kicker">{script === 'architect' ? 'Welcome' : 'Design comments'}</p>
        <h3 id="adt-title" className="adt-title">{step.title}</h3>
        <p className="adt-body">{step.body}</p>
        <div className="adt-footer">
          <span className="adt-dots" aria-hidden>
            {steps.map((_, i) => (
              <span key={i} className={`adt-dot${i === index ? ' adt-dot--on' : ''}`} />
            ))}
          </span>
          {last ? (
            <button type="button" className="adt-next" onClick={finish}>Got it</button>
          ) : (
            <button type="button" className="adt-next" onClick={() => setIndex((i) => i + 1)}>
              Next
            </button>
          )}
        </div>
        <button type="button" className="adt-skip" onClick={finish}>Skip</button>
      </div>
    </div>
  );
};

export default ArchitectDecisionsTour;
